import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trash } from "@phosphor-icons/react";
import { TYPE_COLORS, type BaseField } from "./types";
import { useTranslation } from "@/features/utils/i18n";

interface FieldCardProps {
  field: BaseField;
  index: number;
  onClick: () => void;
  onRemove?: () => void;
  canRemove?: boolean;
}

export const FieldCard = ({
  field,
  index,
  onClick,
  onRemove,
  canRemove = true,
}: FieldCardProps) => {
  const { t } = useTranslation("fieldEditor");
  const handleCardClick = (e: React.MouseEvent) => {
    // Don't trigger onClick if clicking the remove button
    if ((e.target as HTMLElement).closest("[data-remove-button]")) {
      return;
    }
    onClick();
  };

  return (
    <Card
      className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
      onClick={handleCardClick}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Header with type badge and field label */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-muted-foreground font-medium">
              #{index + 1}
            </span>
            <Badge
              variant="secondary"
              className={`${TYPE_COLORS[field.type]} border-0`}
            >
              {t(`types.${field.type}`)}
            </Badge>
          </div>

          {/* Field label */}
          <h4 className="font-medium text-sm mb-1 line-clamp-2">
            {field.label || t("untitled")}
          </h4>

          {/* Optional description */}
          {field.description && (
            <p className="text-xs text-muted-foreground mb-2 line-clamp-1">
              {field.description}
            </p>
          )}

          {/* Options/Details display */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {/* Select - show options */}
            {field.type === "select" &&
              field.options &&
              field.options.length > 0 && (
                <>
                  {field.options.slice(0, 4).map((option) => (
                    <Badge key={option} variant="outline" className="text-xs">
                      {option}
                    </Badge>
                  ))}
                  {field.options.length > 4 && (
                    <Badge variant="outline" className="text-xs">
                      {t("moreOptions", { count: field.options.length - 4 })}
                    </Badge>
                  )}
                </>
              )}

            {/* Number - show min/max and format */}
            {field.type === "number" && (
              <>
                {field.numberFormat && field.numberFormat !== "number" && (
                  <Badge variant="outline" className="text-xs font-medium">
                    {t(`numberFormats.${field.numberFormat}`)}
                  </Badge>
                )}
                {field.min !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    {t("minValue", { value: field.min })}
                  </Badge>
                )}
                {field.max !== undefined && (
                  <Badge variant="outline" className="text-xs">
                    {t("maxValue", { value: field.max })}
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>

        {/* Remove button on the right */}
        {onRemove && canRemove && (
          <div data-remove-button className="shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="xsicon"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              confirm={t("confirmDelete")}
              className="text-destructive hover:text-destructive"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};
